import { useEffect } from 'react';
import axios from 'axios';
import { create } from 'zustand';
import { useSelectedOrganizationName } from '@client/store/organization';
import { getHmrStoreState, initHmrStore } from '@client/helpers/vite.helpers';
import { isPublicProjectOrganization } from '@shared/constants/router.constants';
import {
  CreateSavedScope,
  SavedScope,
  savedScopeSchema,
} from '@shared/models/saved-scope.model';
import { detachForeignSavedScope } from '@features/scope/scope.store';

const STORE_NAME = 'saved-scopes';
const EMPTY: SavedScope[] = [];
type SavedScopesState = {
  organizations: Record<string, SavedScope[]>;
  loadErrors: Record<string, boolean>;
};
const useSavedScopesStore = create<SavedScopesState>(() => ({
  organizations: {},
  loadErrors: {},
  ...getHmrStoreState<SavedScopesState>(STORE_NAME),
}));
initHmrStore(STORE_NAME, useSavedScopesStore);

const configFor = (organization: string) => ({
  headers: { Organization: organization },
});

export const useSavedScopes = () => {
  const organization = useSelectedOrganizationName();
  const scopes = useSavedScopesStore((state) =>
    organization ? (state.organizations[organization] ?? EMPTY) : EMPTY,
  );
  const loadError = useSavedScopesStore((state) =>
    Boolean(organization && state.loadErrors[organization]),
  );
  const canSave = Boolean(
    organization && !isPublicProjectOrganization(organization),
  );
  useEffect(() => {
    if (!organization) return;
    detachForeignSavedScope(organization);
  }, [organization]);
  return { scopes, organization, canSave, loadError };
};

/** Load organization data independently of whether the selector is open. Keeps cached scopes on failure. */
export const loadSavedScopes = async (organization: string): Promise<void> => {
  try {
    const { data } = await axios.get<unknown>(
      'projects/scopes',
      configFor(organization),
    );
    const scopes = savedScopeSchema.array().parse(data);
    useSavedScopesStore.setState((state) => ({
      organizations: { ...state.organizations, [organization]: scopes },
      loadErrors: { ...state.loadErrors, [organization]: false },
    }));
  } catch {
    useSavedScopesStore.setState((state) => ({
      loadErrors: { ...state.loadErrors, [organization]: true },
    }));
  }
};

export const createSavedScope = async (
  organization: string,
  payload: CreateSavedScope,
): Promise<SavedScope> => {
  const { data } = await axios.post<unknown>(
    'projects/scopes',
    payload,
    configFor(organization),
  );
  const saved = savedScopeSchema.parse(data);
  useSavedScopesStore.setState((state) => ({
    organizations: {
      ...state.organizations,
      [organization]: [...(state.organizations[organization] ?? EMPTY), saved],
    },
  }));
  return saved;
};

/** Retire the catalog definition while keeping it resolvable in existing projects. */
export const deleteSavedScope = async (
  organization: string,
  id: string,
): Promise<void> => {
  const { data } = await axios.delete<unknown>(
    `projects/scopes/${id}`,
    configFor(organization),
  );
  const deleted = savedScopeSchema.parse(data);
  useSavedScopesStore.setState((state) => ({
    organizations: {
      ...state.organizations,
      [organization]: (state.organizations[organization] ?? EMPTY).map(
        (scope) => (scope.id === id ? deleted : scope),
      ),
    },
  }));
};

/** Replace an active organization definition after explicit overwrite confirmation. */
export const overwriteSavedScope = async (
  organization: string,
  id: string,
  payload: CreateSavedScope,
): Promise<SavedScope> => {
  const { data } = await axios.put<unknown>(
    `projects/scopes/${id}`,
    payload,
    configFor(organization),
  );
  const saved = savedScopeSchema.parse(data);
  useSavedScopesStore.setState((state) => ({
    organizations: {
      ...state.organizations,
      [organization]: (state.organizations[organization] ?? EMPTY).map(
        (scope) => (scope.id === id ? saved : scope),
      ),
    },
  }));
  return saved;
};
